// Outbox pattern con sort_key por tiempo - CRÍTICO para asistencia en tiempo real

export enum OutboxState {
  PENDING = 'pending',
  SENDING = 'sending',
  ACKED = 'acked',
  ERROR = 'error',
}

export interface OutboxItem {
  id: string;
  meeting_id: string;
  type: 'segment' | 'event' | 'metadata';
  
  // CRÍTICO: sort_key por tiempo (t_ms * 10 + prioridad)
  sort_key: number;
  
  // Para segments
  seq?: number;
  t_start_ms?: number;
  duration_ms?: number;
  format?: 'webm';
  dataRef?: { kind: 'opfs' | 'indexeddb'; path: string };
  
  // Para events y metadata
  data?: any;
  
  // Estado
  state: OutboxState;
  retries: number;
  last_attempt?: number;
  next_attempt_at?: number;
  error_message?: string;
}

// CRÍTICO: Sort key por tiempo (no por tipo)
// t_ms * 10 + prioridad (0=audio, 1=evento, 2=metadata)
export function calculateSortKey(item: Partial<OutboxItem>): number {
  let t_ms = 0;
  let priority = 2; // metadata por defecto
  
  if (item.type === 'segment') {
    t_ms = item.t_start_ms ?? 0;
    priority = 0; // audio ligeramente antes
  } else if (item.type === 'event') {
    t_ms = (item.data as any)?.t_ms ?? 0;
    priority = 1; // eventos
  } else if (item.type === 'metadata') {
    t_ms = (item.data as any)?.timestamp ?? 0;
    priority = 2; // metadata
  }
  
  return t_ms * 10 + priority;
}

export class Outbox {
  private db: IDBDatabase;

  constructor(db: IDBDatabase) {
    this.db = db;
  }

  async enqueue(item: OutboxItem): Promise<void> {
    const tx = this.db.transaction('outbox', 'readwrite');
    await tx.objectStore('outbox').add(item);
  }

  // CRÍTICO: Usar índice by_meeting_sort directamente
  async getNextBatch(meetingId: string, limit: number): Promise<OutboxItem[]> {
    const tx = this.db.transaction('outbox', 'readonly');
    const store = tx.objectStore('outbox');
    const index = store.index('by_meeting_sort');
    
    const items: OutboxItem[] = [];
    const lowerBound = [meetingId, -Infinity];
    const upperBound = [meetingId, Infinity];
    const range = IDBKeyRange.bound(lowerBound, upperBound);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        
        if (cursor && items.length < limit) {
          const item = cursor.value as OutboxItem;
          
          // Filtrar por estado y next_attempt_at
          const now = Date.now();
          const canRetry = !item.next_attempt_at || item.next_attempt_at <= now;
          
          if (item.state === OutboxState.PENDING && canRetry) {
            items.push(item);
          }
          
          cursor.continue();
        } else {
          resolve(items); // Ya vienen ordenados por sort_key del índice
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async getSendingItems(meetingId: string): Promise<OutboxItem[]> {
    const tx = this.db.transaction('outbox', 'readonly');
    const store = tx.objectStore('outbox');
    const index = store.index('by_meeting_state');
    
    const items: OutboxItem[] = [];
    const range = IDBKeyRange.only([meetingId, OutboxState.SENDING]);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        
        if (cursor) {
          items.push(cursor.value as OutboxItem);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async recoverSendingItems(meetingId: string): Promise<void> {
    const tx = this.db.transaction('outbox', 'readwrite');
    const store = tx.objectStore('outbox');
    const index = store.index('by_meeting_state');
    const range = IDBKeyRange.only([meetingId, OutboxState.SENDING]);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        
        if (cursor) {
          const item = cursor.value as OutboxItem;
          item.state = OutboxState.PENDING;
          cursor.update(item);
          cursor.continue();
        } else {
          resolve();
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async markSending(id: string): Promise<void> {
    const tx = this.db.transaction('outbox', 'readwrite');
    const store = tx.objectStore('outbox');
    const request = store.get(id);
    const item = await new Promise<OutboxItem | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (item) {
      item.state = OutboxState.SENDING;
      item.last_attempt = Date.now();
      await store.put(item);
    }
  }

  async markAcked(id: string): Promise<void> {
    const tx = this.db.transaction('outbox', 'readwrite');
    const store = tx.objectStore('outbox');
    const request = store.get(id);
    const item = await new Promise<OutboxItem | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (item) {
      item.state = OutboxState.ACKED;
      await store.put(item);
    }
  }

  // CRÍTICO: markError con next_attempt_at (backoff exponencial)
  async markError(id: string, error: string): Promise<void> {
    const tx = this.db.transaction('outbox', 'readwrite');
    const store = tx.objectStore('outbox');
    const request = store.get(id);
    const item = await new Promise<OutboxItem | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    
    if (item) {
      item.state = OutboxState.ERROR;
      item.error_message = error;
      item.retries++;
      
      // Backoff: 5s, 10s, 20s, 40s, 80s
      const backoffMs = Math.min(5000 * Math.pow(2, item.retries - 1), 80000);
      item.next_attempt_at = Date.now() + backoffMs;
      
      await store.put(item);
    }
  }

  // CRÍTICO: Incluir ERROR en pending count
  async getPendingCount(meetingId: string): Promise<number> {
    const tx = this.db.transaction('outbox', 'readonly');
    const store = tx.objectStore('outbox');
    const index = store.index('by_meeting');
    const range = IDBKeyRange.only(meetingId);
    
    let count = 0;
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        
        if (cursor) {
          const item = cursor.value as OutboxItem;
          if ([OutboxState.PENDING, OutboxState.SENDING, OutboxState.ERROR].includes(item.state)) {
            count++;
          }
          cursor.continue();
        } else {
          resolve(count);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  // CRÍTICO: Forzar resincronización (ERROR → PENDING)
  async retryAllErrors(meetingId: string): Promise<void> {
    const tx = this.db.transaction('outbox', 'readwrite');
    const store = tx.objectStore('outbox');
    const index = store.index('by_meeting_state');
    const range = IDBKeyRange.only([meetingId, OutboxState.ERROR]);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        
        if (cursor) {
          const item = cursor.value as OutboxItem;
          item.state = OutboxState.PENDING;
          item.next_attempt_at = undefined;
          cursor.update(item);
          cursor.continue();
        } else {
          resolve();
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  // Get error items for debugging/display
  async getErrorItems(meetingId: string): Promise<OutboxItem[]> {
    const tx = this.db.transaction('outbox', 'readonly');
    const store = tx.objectStore('outbox');
    const index = store.index('by_meeting_state');
    const range = IDBKeyRange.only([meetingId, OutboxState.ERROR]);
    
    const items: OutboxItem[] = [];
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);
      
      request.onsuccess = () => {
        const cursor = request.result;
        
        if (cursor) {
          items.push(cursor.value as OutboxItem);
          cursor.continue();
        } else {
          resolve(items);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }
}

